import { publishGenerationStatus } from 'Pods/Shared/GenerationStatus/actions'

import { threeDStudioStore } from '../index'

const setGenerationState = (patch) => {
  threeDStudioStore.setState((state) => ({
    generation: { ...state.generation, ...patch },
  }))
  // Mirror live status into the global store so any pack (top nav, etc.) can read it.
  publishGenerationStatus(patch)
}

// The single-run scalar fields (isGenerating, runId, ...) predate concurrent
// generations and are still read by single-run consumers (viewer banner, collapsed
// card, the global GenerationStatus mirror). They track the most recently started
// in-flight run; with no runs left they reset to their defaults.
const scalarRunFieldsFor = (run) => (run
  ? {
    isGenerating: true,
    isEnhancing: run.enhancingSessionId != null,
    triggeredBy: run.triggeredBy ?? null,
    enhancingSessionId: run.enhancingSessionId,
    generationStartedAt: run.startedAt ?? null,
    runId: run.runId,
    sessionId: run.sessionId,
    lastGenerationSettings: run.settings ?? {},
  }
  : {
    isGenerating: false,
    isEnhancing: false,
    triggeredBy: null,
    enhancingSessionId: undefined,
    generationStartedAt: null,
    runId: undefined,
    sessionId: undefined,
    lastGenerationSettings: {},
  })

/**
 * Returns the generation-slice fields that must survive a slice reset while runs
 * are in flight: the run list itself, the scalar mirror of its latest run, and the
 * live wallet balance. Spread over DEFAULT_GENERATION when resetting the slice.
 * @param {object} generation - The current generation slice
 */
export const activeRunStateOf = (generation) => ({
  activeRuns: generation.activeRuns,
  availableCredits: generation.availableCredits,
  ...scalarRunFieldsFor(generation.activeRuns[generation.activeRuns.length - 1]),
})

/**
 * Registers a newly started (or restored) in-flight run and points the single-run
 * scalar fields at it. Starting a run also clears any previous failure state.
 * @param {object} run - { clientId, runId?, sessionId?, triggeredBy, isEnhancement,
 *   enhancingSessionId?, startedAt, settings? }
 */
export const addActiveRun = (run) => {
  let patch = {}
  threeDStudioStore.setState((state) => {
    patch = {
      activeRuns: [...state.generation.activeRuns, run],
      ...scalarRunFieldsFor(run),
      hasFailed: false,
      errorDetails: {},
      errorId: '',
    }
    return { generation: { ...state.generation, ...patch } }
  })
  publishGenerationStatus(patch)
}

/**
 * Merges a patch into one in-flight run (matched by clientId). When the patched run
 * is the latest one, the single-run scalar fields are refreshed from it too.
 * @param {string} clientId - The run's client-generated id
 * @param {object} runPatch - Fields to merge into the run (runId, sessionId, settings)
 */
export const updateActiveRun = (clientId, runPatch) => {
  let patch = {}
  threeDStudioStore.setState((state) => {
    const activeRuns = state.generation.activeRuns.map((run) => (
      run.clientId === clientId ? { ...run, ...runPatch } : run
    ))
    const latest = activeRuns[activeRuns.length - 1]
    patch = {
      activeRuns,
      ...(latest?.clientId === clientId ? scalarRunFieldsFor(latest) : {}),
    }
    return { generation: { ...state.generation, ...patch } }
  })
  publishGenerationStatus(patch)
}

/**
 * Removes a settled (completed/failed/rejected) run and re-points the single-run
 * scalar fields at the most recent remaining run, if any.
 * @param {{ clientId?: string, runId?: number|string }} key - Matched by clientId
 *   when given, otherwise by runId
 */
export const removeActiveRun = ({ clientId, runId }) => {
  let patch = {}
  threeDStudioStore.setState((state) => {
    const activeRuns = state.generation.activeRuns.filter((run) => (
      clientId != null
        ? run.clientId !== clientId
        : String(run.runId) !== String(runId)
    ))
    patch = {
      activeRuns,
      ...scalarRunFieldsFor(activeRuns[activeRuns.length - 1]),
    }
    return { generation: { ...state.generation, ...patch } }
  })
  publishGenerationStatus(patch)
}

/**
 * Sets whether a generation is in progress
 * @param {boolean} isGenerating - True when generation is running
 * @param {number|string|undefined} generationStartedAt - Timestamp or date string when generation started
 */
export const setIsGenerating = (isGenerating, generationStartedAt) => {
  if (isGenerating && generationStartedAt) {
    const date = new Date(generationStartedAt)
    const dateInMilliseconds = date.getTime()
    setGenerationState({ isGenerating, generationStartedAt: dateInMilliseconds })

    return
  }

  setGenerationState({
    isGenerating,
    ...(isGenerating
      ? { hasFailed: false, errorDetails: {}, errorId: '' }
      : { isEnhancing: false, triggeredBy: null }),
    generationStartedAt: isGenerating ? Date.now() : null,
  })
}

/**
 * Marks whether the in-flight generation is an enhancement (remesh/retexture/
 * segment) rather than an initial generation. Drives the "Processing..." sidebar
 * state. Cleared automatically when generation ends (see setIsGenerating).
 * @param {boolean} isEnhancing - True when an enhancement run is in progress
 */
export const setIsEnhancing = (isEnhancing) => {
  setGenerationState({ isEnhancing })
}

/**
 * Records which action button started the in-flight run so only that button shows
 * the in-progress label; every other action button is disabled with an "in
 * progress" tooltip. Cleared when generation ends (see setIsGenerating).
 * @param {string|null} triggeredBy - The triggering action key (GENERATION_TRIGGER_KEY
 *   for an initial generation, or the enhancement tab id for an enhancement run)
 */
export const setTriggeredBy = (triggeredBy) => {
  setGenerationState({ triggeredBy })
}

/**
 * Sets the session id of the model whose enhancement run is in progress. Drives
 * the "Processing..." card for that session in the library. On reload the value is
 * re-derived from the pending-runs response (see usePendingGeneratorRun).
 * @param {number|string|undefined} enhancingSessionId - The enhanced model's session id
 */
export const setEnhancingSessionId = (enhancingSessionId) => {
  setGenerationState({ enhancingSessionId })
}

/**
 * Sets whether a generation has completed at least once
 * @param {boolean} hasGenerated - True after the first successful generation
 */
export const setHasGenerated = (hasGenerated) => {
  setGenerationState({ hasGenerated })
}

/**
 * Sets the current generation run id
 * @param {number|undefined} runId - The run id from the current generation
 */
export const setRunId = (runId) => {
  setGenerationState({ runId })
}

/**
 * Sets the current generation session id
 * @param {number|undefined} sessionId - The session id from the current generation
 */
export const setSessionId = (sessionId) => {
  setGenerationState({ sessionId })
}

/**
 * Sets the last generation settings
 * @param {object} lastGenerationSettings - The last generation settings
 */
export const setLastGenerationSettings = (lastGenerationSettings) => {
  setGenerationState({ lastGenerationSettings })
}

/**
 * Sets the available credits
 * @param {number} availableCredits - The available credits
 */
export const setAvailableCredits = (availableCredits) => {
  setGenerationState({ availableCredits })
}

/**
 * Sets the generation has failed
 * @param {boolean} hasFailed - True when generation has failed
 */
export const setHasFailed = (hasFailed) => {
  setGenerationState({ hasFailed })
}

/**
 * Sets the generation error id
 * @param {string} errorId - The error id from the current generation
 */
export const setErrorId = (errorId) => {
  setGenerationState({ errorId })
}

/**
 * Sets the generation error details (failureReason, failureMessage, providerErrorCode)
 * @param {object} errorDetails - The structured error payload from the backend
 */
export const setErrorDetails = (errorDetails) => {
  setGenerationState({ errorDetails: errorDetails || {} })
}
